import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import { usePolling } from "@common/hooks";
import { getMonitoringMeetings } from "@services/monitoring";
import { Device } from "@services/devices/types";
import { getMeetingAnnotations } from "../utils";
import { MeetingInstance } from "@services/meetings/types";

type UseMeetingAnnotationsParams = {
  device: Device | undefined;
  start: number;
  end: number;
  liveUpdate: boolean;
  breadcrumbPage: string;
};

export const useMeetingAnnotations = (params: UseMeetingAnnotationsParams) => {
  const { device, start, end, liveUpdate, breadcrumbPage } = params;
  
  const navigate = useNavigate();
  const theme = useTheme();
  const [meetingList, setMeetingList] = useState<MeetingInstance[]>([]);

  const room = device && device.room;

  const fetchMeetingsList = async (start: number, end: number) => {
    if (!room) return [];

    const meetings = await getMonitoringMeetings(start, end, room.id ?? "");

    return meetings.list;
  };

  const refetch = useCallback(async () => {
    try {
      const meetings = await fetchMeetingsList(start, end);
      setMeetingList(meetings);
    } catch (e) {}
  }, [start, end]);

  const poll = usePolling(refetch, 60e3);

  useEffect(() => {
    if (liveUpdate && !poll.isPolling) {
      poll.start();
    }

    if (!liveUpdate && poll.isPolling) {
      poll.stop();
    }

    return () => {
      poll.stop();
    };
  }, [liveUpdate]);

  return useMemo(
    () => ({
      annotations: getMeetingAnnotations(
        meetingList,
        { start, end },
        theme,
        navigate,
        breadcrumbPage!
      ),
      refetch,
    }),
    [meetingList, theme, refetch, breadcrumbPage]
  );
};
